import { Link } from 'react-router';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import { Title } from '../../utils/title';
import Base from '../Base';
import styles from './ChatGPT.module.scss';
import './common.css';

import _01 from '../../assets/custom/chatgpt/01.png';
import _02 from '../../assets/custom/chatgpt/02.gif';
import _03 from '../../assets/custom/chatgpt/03.png';
import _04 from '../../assets/custom/chatgpt/04.png';
import _05 from '../../assets/custom/chatgpt/05.png';
import _06 from '../../assets/custom/chatgpt/06.png';
import _07 from '../../assets/custom/chatgpt/07.png';
import _08 from '../../assets/custom/chatgpt/08.png';

const ChatGPT = () => {

  const creationDate = 'May 30, 2023';
  const lastEditedDate = 'June 1, 2023';

  return (
    <Base>
      <div className={styles.root}>

        <Title value='Harness the Power of ChatGPT Prompts' />

        {
          /*
           * BEGIN HTML
           */
        }
        <div className="blog-post container">
          <div className="row">
            <div id="main-content" className="col-12">
              <h1 className="blog-post__title">
                Harness the Power of ChatGPT Prompts: A Comprehensive Guide to Analyzing Your Product
                Reviews
              </h1>
              <p className="blog-post__meta">
                Created on {creationDate} | Last edited on {lastEditedDate}
              </p>
              <LazyLoadImage
                src={_01}
                alt="Blog post cover that has magnifying class looking at a text review"
                className="blog-post__image img-fluid"
              />
              <p className="blog-post__p">
                The importance of dissecting product reviews in today's market climate cannot be
                overstated. It's instrumental in comprehending consumer behavior, refining product
                quality, and elevating your brand's standing. Traditionally, this process relied on
                specialized software that often came with a substantial cost and demanded dedicated
                training. But thanks to the advent of AI technologies like ChatGPT, this scenario has
                undergone a radical transformation. With this free, user-friendly tool, you can analyze
                extensive product reviews, uncover valuable insights, and present them in a way that
                anyone can understand. This data analysis democratization opens the door for anyone to
                gauge customer sentiment, pinpoint product strengths and weaknesses, and make
                well-informed decisions for product enhancements. This, in turn, enriches customer
                satisfaction and stimulates business growth.
              </p>
              <br></br>
              <h2 className="blog-post__subtitle">Collecting Review Data</h2>
              <p className="blog-post__p">
                Analyzing product reviews with ChatGPT presents a powerful asset for brand managers, but
                it comes with a unique set of challenges. The process begins with assembling and
                cleaning your review data. If your review data is neatly filed in a CSV file, feel free
                to skip this section and dive straight into the analysis portion.
              </p>
              <p className="blog-post__p">
                Before we get started, it's crucial to remember that when supplying data to ChatGPT,
                particularly if it's not publicly accessible, you must ensure that no sensitive or
                personally identifiable information is included. Upholding your customers' privacy and
                sticking to data protection regulations should always be your foremost concern.
              </p>
              <p className="blog-post__p">
                Now, let's delve into the process of collecting review data. Initially, we were
                optimistic that ChatGPT could seamlessly extract review data for us by merely supplying
                a link, like "www.big-ecommerce-site.com/awesome-product/all-reviews". Regrettably, it's
                not as simple as that. Indeed, we even delayed this article's publication to investigate
                ChatGPT's newly launched browsing support and dabbled with Google Bart, which possesses
                innate browsing capabilities. But most sizable e-commerce sites implement measures to
                deter bots like ChatGPT and Google Bart from scraping their review content.
              </p>
              <p className="blog-post__p">For example, if you try a prompt like:</p>
              <div className="chatgpt-prompt">
                <p>
                  Extract all product reviews from the following link:
                  www.big-ecommerce-site.com/awesome-product/all-reviews and format them into a table
                  with columns indicating star rating, review time, and review text.
                </p>
              </div>
              <p className="blog-post__p">you'll likely face an error message like this:</p>
              <LazyLoadImage
                src={_02}
                alt="Screenshot showing ChatGPT error while browsing"
                className="blog-screenshot img-fluid"
              />
              <br></br>
              <br></br>
              <p className="blog-post__p">
                So, what can you do if your review data isn't readily available? Don't fret, there are
                still efficient ways to collect the information you need. In the forthcoming sections,
                we'll examine alternative methods that will allow you to acquire your desired review
                data and proceed with the analysis stage.
              </p>
              <p className="blog-post__p">
                Almost all e-commerce sites dedicate a page to a product's reviews. Navigate to that
                page, select all, and copy the text. On a Mac, you can do this by using Command+A and
                Command+C, while on Windows, it's Ctrl+A and Ctrl+C.
              </p>
              <LazyLoadImage
                src={_03}
                alt="Gif showing how to copy all text on a website"
                className="blog-gif img-fluid"
              />
              <p className="blog-post__p">
                Now, open your preferred text editor and paste the content. If the reviews are spread
                over multiple pages, navigate to the next page and copy, paste, and append these reviews
                to the text editor as well.
              </p>
              <div className="text-editor">
                <p>
                  <strike>
                    {' '}
                    Awsome Product <br></br> This product will change your life! <br></br> All Customer
                    Reviews
                  </strike>{' '}
                  <br></br> John Doe<br></br> 4 out of 5 stars
                  <br></br> May 29, 2023 <br></br>Lorem ipsum dolor sit amet, consectetur adipiscing
                  elit. <br></br>Jane Doe
                  <br></br> 3 out of 5 stars <br></br>May 28, 2023 <br></br>Sed do eiusmod tempor
                  incididunt ut labore et dolore magna aliqua. <br></br>{' '}
                  <strike>
                    {' '}
                    chimeable <br></br>Instagram <br></br>Facebook TikTok <br></br>YouTube Shorts{' '}
                    <br></br>
                    Copyright ©2023 Chimeable. <br></br>All Rights Reserved. <br></br>Terms & Conditions
                    | Cookies Policy | Privacy Policy<br></br> Cookies help us deliver our services. By
                    using our website, you agree to our use of cookies.
                  </strike>
                </p>
              </div>
              <p className="blog-post__p">
                Notice that part of the text at the top and bottom is stricken out. We suggest removing
                these texts that aren't related to reviews. ChatGPT is intelligent enough to overlook
                these texts. However, there's a size limit on the prompt, and we wouldn't want to
                squander it by sending superfluous data. Cleaning up should not be very hard since the
                text you need to remove will only be at the top and bottom.
              </p>
              <p className="blog-post__p">
                The ChatGPT prompt size limit we referenced is 4096 tokens, which can be broadly
                understood as words. This limit roughly equates to about 30-40 average-length reviews.
                However, to ensure you stay within the token limit, we suggest feeding reviews to
                ChatGPT in segments of no more than 25 reviews at a time. To achieve this, you should
                start with the following prompt.
              </p>
              <div className="chatgpt-prompt">
                <p>
                  I will provide you a large text in multiple parts that includes product reviews. Once
                  you get the text, ask for the next part of the text. No need to print anything.
                </p>
              </div>
              <div className="chatgpt-prompt-response">
                <p>
                  Sure, I can help you with that. Please provide the first part of the text, and then
                  let me know when you're ready to proceed with the next part.
                </p>
              </div>
              <div className="chatgpt-prompt">
                <p>
                  John Doe<br></br> 4 out of 5 stars
                  <br></br> May 29, 2023 <br></br>Lorem ipsum dolor sit amet, consectetur adipiscing
                  elit. <br></br>Jane Doe
                  <br></br> 3 out of 5 stars <br></br>May 28, 2023 <br></br>Sed do eiusmod tempor
                  incididunt ut labore et dolore magna aliqua. <br></br>{' '}
                </p>
              </div>
              <div className="chatgpt-prompt-response">
                <p>
                  Thank you for providing the next part of the text. Please provide the next part, and
                  I'll continue with the processing.
                </p>
              </div>
              <p className="blog-post__p">
                Once you've completed inserting reviews, you can inform ChatGPT that you're done. Now
                ask it to extract reviews from the text and print the reviews in tabular form.
              </p>
              <div className="chatgpt-prompt">
                <p>
                  "We are done. Now, extract reviews from the entire text and arrange them in a table
                  with columns: star rating, date, review text, and reviewer.
                </p>
              </div>
              <LazyLoadImage
                src={_04}
                alt="Screenshot of sample review in tabular form"
                className="blog-screenshot img-fluid"
              />
              <br></br>
              <br></br>
              <p className="blog-post__p">
                If you see your reviews in tabular form without any issues, that means the data import
                was successful, and we can continue with the next step.{' '}
              </p>
              <br></br>
              <h2 className="blog-post__subtitle">Providing Context and Role</h2>
              <p className="blog-post__p">
                It's crucial to provide ChatGPT with context and role as it aids in crafting accurate
                and appropriate responses. The context gives the AI a better understanding of the user's
                situation, needs, or goals, while the role ensures that the AI embodies the necessary
                characteristics, capabilities, or responsibilities for a given interaction, resulting in
                more relevant and meaningful conversations.
              </p>
              <p className="blog-post__p">
                Please make sure to include the name of your product as well as its category.
                Optionally, you can provide the main targeted demographic and any recent major overhauls
                on the product
              </p>
              <div className="chatgpt-prompt">
                <p>
                  We have a product named <b>[Product Name]</b> in the
                  <b> [Most Detailed Product Category]</b> category. The product is primarily marketed
                  to
                  <b> [Target Demographic]</b>. The product underwent a major overhaul on <b>[Date]</b>.
                  We want to conduct some analysis on the previously provided reviews. For this purpose,
                  going forward, I would like you to act as a data analyst. I will provide the analysis
                  I require in the subsequent prompts. Please hold.
                </p>
              </div>
              <div className="chatgpt-prompt-response">
                <p>
                  Sure, I can act as a data analyst for your analysis of <b>[Product Name]</b> reviews.
                  Please provide the analysis requirements, and I'll be ready to assist you.
                </p>
              </div>
              <p className="blog-post__p">
                We've completed our setup, which was the most demanding part of this process. We will
                provide a couple of example analyses. You can effortlessly come up with new ones that
                will furnish you with the insights you seek.
              </p>
              <br></br>
              <h2 className="blog-post__subtitle">Sentiment Analysis</h2>
              <p className="blog-post__p">
                By scrutinizing the sentiment expressed in reviews (positive, negative, or neutral), you
                can measure overall customer satisfaction and identify trends in your product or service
                reception.
              </p>
              <div className="chatgpt-prompt">
                <p>
                  Given the reviews, would you say the sentiment expressed towards the product is
                  positive, negative, or neutral for each review? Add the result as separate column.
                </p>
              </div>
              <LazyLoadImage
                src={_05}
                alt="Screenshot of sample reviews with sentiment in tabular form"
                className="blog-screenshot img-fluid"
              />
              <br></br>
              <br></br>
              <div className="chatgpt-prompt">
                <p>What would you say is the overall sentiment?</p>
              </div>
              <div className="chatgpt-prompt-response">
                <p>
                  {' '}
                  Based on the sentiment analysis of the reviews provided, the overall sentiment towards
                  the
                  <b> [Product Name]</b> appears to be predominantly positive.
                </p>
              </div>
              <h2 className="blog-post__subtitle">Text Analytics</h2>
              <p className="blog-post__p">
                By parsing and categorizing the text of product reviews, common themes, keywords, and
                phrases can be identified. This information can help you comprehend what customers
                appreciate or dislike about your products or services.
              </p>
              <div className="chatgpt-prompt">
                <p>
                  Perform text analytics on the reviews and provide the top three common phrases
                  mentioned.
                </p>
              </div>
              <div className="chatgpt-prompt-response">
                <p>
                  To identify the top three common phrases mentioned in the reviews, I will perform text
                  analytics and analyze the review text. Here are the top three common phrases:{' '}
                  <br></br>{' '}
                  <ul>
                    <li>"Phrase 1": Some explanation</li>
                    <li>"Phrase 2": Some explanation</li>
                    <li>"Phrase 3": Some explanation</li>
                  </ul>{' '}
                  These phrases are frequently mentioned in the reviews, reflecting the key aspects that
                  customers appreciate about the <b> [Product Name]</b>.
                </p>
              </div>
              <h2 className="blog-post__subtitle">Rating Analysis</h2>
              <p className="blog-post__p">
                Analyzing the distribution of ratings (e.g., 1-5 stars) can provide an overview of
                customer satisfaction.
              </p>
              <div className="chatgpt-prompt">
                <p>Could you show the distribution of star ratings?</p>
              </div>
              <LazyLoadImage
                src={_06}
                alt="Screenshot of sample review stars distribution in tabular form"
                className="blog-screenshot img-fluid"
              />
              <br></br>
              <br></br>
              <h2 className="blog-post__subtitle">Feature Analysis</h2>
              <p className="blog-post__p">
                Identifying specific product features or attributes mentioned in reviews can aid in
                understanding what aspects of your product are most valued or need improvement.
              </p>
              <div className="chatgpt-prompt">
                <p>
                  Please perform feature analysis on the reviews and find the most critical aspect that
                  needs improvement.
                </p>
              </div>
              <div className="chatgpt-prompt-response">
                <p>
                  To perform feature analysis on the reviews and identify the most important aspect that
                  needs improvement, I will analyze the review text to identify common keywords or
                  phrases related to areas of improvement. Here are the aspects that were frequently
                  mentioned for improvement: <br></br>{' '}
                  <ul>
                    <li>"Feature 1": Some explanation</li>
                    <li>"Feature 2": Some explanation</li>
                    <li>"Feature 3": Some explanation</li>
                  </ul>{' '}
                </p>
              </div>
              <h2 className="blog-post__subtitle">Time Series Analysis</h2>
              <p className="blog-post__p">
                Investigating how reviews and ratings change over time can disclose trends in product
                performance, customer satisfaction, and the impact of updates or alterations to the
                product.
              </p>
              <div className="chatgpt-prompt">
                <p>
                  Perform time series analysis on the reviews and present in table format how the
                  average rating evolves over time. Group the reviews quarterly.
                </p>
              </div>
              <LazyLoadImage
                src={_07}
                alt="Screenshot of sample review average ratings per quarter in tabular form"
                className="blog-screenshot img-fluid"
              />{' '}
              <br></br>
              <br></br>
              <h2 className="blog-post__subtitle">Exporting Data</h2>
              <p className="blog-post__p">
                Once your analysis is complete, you can export your data to visualize or share using
                different tools. You can easily export your tabular results in CSV format. Here is an
                example for exporting time series analysis results.
              </p>
              <div className="chatgpt-prompt">
                <p>Please export the time series analysis data in CSV format.</p>
              </div>
              <LazyLoadImage
                src={_08}
                alt="Screenshot of sample review average ratings per quarter in CSV format"
                className="blog-screenshot img-fluid"
              />{' '}
              <br></br>
              <br></br> <h2 className="blog-post__subtitle">Other Analyses</h2>
              <p className="blog-post__p">
                You could also conduct the following analyses: Comparative Analysis, Demographic
                Analysis, Customer Segmentation. However, these analyses necessitate importing
                additional data, which you can do as we demonstrated at the beginning of this article.
              </p>
              <br></br>
              <div className="customBox text-light font-redhat-regular ">
                <p>
                  Do you need more reviews for your product to increase your sales and provide more
                  insight? Chimeable can help you.
                </p>
                <div className="buttonWrapper">
                  <Link
                    to="?dialog=contact"
                    className="blog-button btn rounded-5 text-dark fw-bold font-redhat-regular"
                    aria-label="book a demo">
                    Get More Reviews
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        {
          /*
           * END HTML
           */
        }

      </div>
    </Base>
  );
};

export default ChatGPT;